/** The playhead: where the sound is, right now, shared by whoever draws it.
 *
 *  A plain mutable object rather than React state, and that is the whole point
 *  of the file. A clip is drawn at sixty frames a second by three different
 *  things -- a map marker moving along an AIS track, a blob breathing, a
 *  progress bar -- and routing that through `useState` re-renders the hour
 *  detail sixty times a second to move a triangle four pixels. The player
 *  writes here; each visualisation runs its own `requestAnimationFrame` and
 *  reads. Nothing re-renders.
 *
 *  It is deliberately not a store with subscribers. There is exactly one
 *  producer, the readers are all animation loops that tick anyway, and a
 *  subscription would only add a way for a reader to be woken up at the wrong
 *  time.
 */
export interface Playhead {
  /** Seconds into the clip, or null when nothing is playing. Null rather than
   *  zero: "stopped" and "at the start" are different states, and a marker
   *  parked at second zero of an hour is a claim about where a vessel was. */
  t: number | null
  /** Clip length in seconds; 0 until the file is decoded. */
  duration: number
  playing: boolean
  /** Epoch seconds of the clip's first sample, 0 when unknown.
   *
   *  This is what ties the audio to the world: without it a playhead is an
   *  offset into a file, and with it the same number is a moment a vessel was
   *  somewhere. */
  startEpoch: number
}

export function newPlayhead(): Playhead {
  return { t: null, duration: 0, playing: false, startEpoch: 0 }
}

/** Wall-clock epoch of the playhead, or null when nothing is playing or the
 *  clip's own start time is unknown. Never guessed. */
export function playheadEpoch(p: Playhead): number | null {
  if (p.t === null || !p.startEpoch) return null
  return p.startEpoch + p.t
}
